const UTC_ZONE = 'UTC'

function abbreviation(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'short'
  }).formatToParts(date)
  return parts.find(part => part.type === 'timeZoneName')?.value ?? timeZone
}

/**
 * The zone's STANDARD-time abbreviation — EST rather than EDT — regardless of
 * whether daylight saving is currently in effect. Standard abbreviations are the
 * stable, recognisable name of a region, so they read better in an operational
 * context where a reader shouldn't have to track the DST calendar.
 *
 * Zones with no daylight saving (IST, UTC, JST) return their only abbreviation,
 * and zones whose "abbreviation" is a numeric offset (GMT+5:30) pass through.
 */
function standardAbbreviation(timeZone: string): string {
  const year = new Date().getUTCFullYear()

  // Midwinter in each hemisphere: one of these is always standard time.
  const january = abbreviation(new Date(Date.UTC(year, 0, 15, 12)), timeZone)
  const july = abbreviation(new Date(Date.UTC(year, 6, 15, 12)), timeZone)

  if (january === july) return january

  // Daylight abbreviations carry a "D" (EDT, PDT, CEST → S in position 2).
  const isDaylight = (value: string) => /^[A-Z]D[A-Z]$/.test(value) || /^[A-Z]{2,}S T?$/.test(value)
  if (isDaylight(january) && !isDaylight(july)) return july
  if (isDaylight(july) && !isDaylight(january)) return january

  // Northern hemisphere zones are on standard time in January; southern in July.
  const januaryOffset = offsetMinutes(new Date(Date.UTC(year, 0, 15, 12)), timeZone)
  const julyOffset = offsetMinutes(new Date(Date.UTC(year, 6, 15, 12)), timeZone)
  return januaryOffset <= julyOffset ? january : july
}

/** Minutes offset from UTC for a zone at a given instant. */
function offsetMinutes(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'longOffset'
  }).formatToParts(date)
  const name = parts.find(part => part.type === 'timeZoneName')?.value ?? ''
  const match = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(name)
  if (!match) return 0

  const sign = match[1] === '-' ? -1 : 1
  return sign * (Number(match[2]) * 60 + Number(match[3] ?? 0))
}

/** The visitor's IANA time zone, e.g. "America/New_York". */
export function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || UTC_ZONE
  } catch {
    return UTC_ZONE
  }
}

/** Standard-time label for a zone, e.g. "EST". Falls back to the zone id. */
export function timeZoneLabel(timeZone: string): string {
  try {
    return standardAbbreviation(timeZone)
  } catch {
    return timeZone
  }
}

/** e.g. "1:00 PM EST" — clock time is real, the label is the standard abbreviation. */
export function formatTime(date: Date, timeZone: string): string {
  const time = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date)
  return `${time} ${timeZoneLabel(timeZone)}`
}

/** Current wall-clock time in a zone, e.g. "3:42 AM". Empty when unrecognised. */
export function currentTimeIn(timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(new Date())
  } catch {
    return ''
  }
}

export { UTC_ZONE }
